import { BoxV2 as Box, Button, Flex } from 'portal-commons';
import {
  FunctionComponent,
  ImgHTMLAttributes,
  useEffect,
  useState,
} from 'react';

import { brandAssetController, attachmentController } from '@/apis/core';
import { Modal } from '@/components';
import { toastFlashMessage } from '@/utils';

interface AssetVerificationModalProps {
  open?: boolean;
  brandUid: string;
  type?: 'LOGO' | 'BANNER';
  name?: string;
  uuid?: string;
  url?: string;
  onCancel?: () => void;
  onSubmit?: () => void;
}

const Title: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      padding: '12px 0',
      color: 't.black80',
      fontWeight: 600,
      fontSize: 'H600',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Message: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      paddingBottom: 'xs',
      color: 't.black80',
      fontWeight: 400,
      fontSize: 'H400',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const BoldText: FunctionComponent = ({ children }) => (
  <Box as="span" sx={{ fontWeight: 600 }}>
    {children}
  </Box>
);

const Preview: FunctionComponent<
  ImgHTMLAttributes<HTMLImageElement> & { type: string }
> = ({ type, ...props }) => (
  <Box
    sx={{
      width: type === 'LOGO' ? '195px' : '408px',
      height: type === 'LOGO' ? '195px' : '127px',
      borderRadius: type === 'LOGO' ? '50%' : 'none',
      overflow: 'hidden',
      '& > img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
    }}
  >
    <img {...props} />
  </Box>
);

const Actions: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: '18px',
      padding: '30px 0',
    }}
  >
    {children}
  </Flex>
);

const AssetVerificationModal: FunctionComponent<
  AssetVerificationModalProps
> = ({
  open,
  brandUid,
  type = 'LOGO',
  name,
  uuid,
  url,
  onCancel,
  onSubmit,
}) => {
  const [imageUrl, setImageUrl] = useState(url);
  const [proceeding, setProceeding] = useState(false);

  const handleVerify = async () => {
    setProceeding(true);
    const response = await brandAssetController.verifyAsset(
      brandUid,
      name ?? ''
    );
    if (response) {
      toastFlashMessage(
        `${name} asset successfully submitted for verification`,
        'success'
      );
      onSubmit?.();
    }
    setProceeding(false);
  };

  useEffect(() => {
    if (uuid) {
      let newImageUrl: string | undefined;
      (async () => {
        const file = await attachmentController.downloadAttachment({ uuid });
        if (file) {
          newImageUrl = URL.createObjectURL(file);
          setImageUrl(newImageUrl);
        } else {
          setImageUrl(url);
        }
      })();
      return () => {
        if (newImageUrl) {
          URL.revokeObjectURL(newImageUrl);
        }
      };
    }
  }, [uuid]);

  useEffect(() => {
    setImageUrl(url);
  }, [url]);

  return (
    <Modal style={{ width: '483px' }} open={open}>
      <Title>Asset Verification</Title>
      <Message>
        This asset has not been verified. Verification is required for use in an
        RCS campaign.
        <br />
        <br />
        <>
          A one-time, non-refundable charge of {type === 'LOGO' ? '$75' : '$50'}{' '}
          is required to verify a {type} and will be added to your balance.
        </>
        <br />
        <br />
        Click <BoldText>I Agree</BoldText> to proceed.
      </Message>
      {imageUrl && <Preview type={type} src={imageUrl} />}
      <Actions>
        <Button disabled={proceeding} variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={proceeding} onClick={handleVerify}>
          {proceeding ? 'Processing...' : 'I Agree'}
        </Button>
      </Actions>
    </Modal>
  );
};

export default AssetVerificationModal;
